import { PACKAGE_TIERS } from './definition';

/**
 * Groups fields by their category prefix (e.g., env.AWS, brand.FeatureFlag)
 * @param {Object} fields - The allFields object
 * @returns {Object} - Grouped fields by category
 */
export function groupFieldsByCategory(fields) {
  const grouped = {};

  Object.entries(fields).forEach(([key, value]) => {
    // Extract category from key (e.g., "env__aws__s3__region" -> "AWS")
    const parts = key.split('__');

    if (parts.length < 2) return;

    const prefix = parts[0]; // "env" or "brand"
    const category = parts[1]; // "aws", "firebase", "feature_flag", etc.

    const categoryKey = `${prefix}.${category}`;
    const categoryLabel = formatCategoryLabel(prefix, category);

    if (!grouped[categoryKey]) {
      grouped[categoryKey] = {
        label: categoryLabel,
        fields: [],
      };
    }

    grouped[categoryKey].fields.push({
      key,
      ...value,
    });
  });

  return grouped;
}

/**
 * Formats category label for display
 * @param {string} prefix - The prefix (env or brand)
 * @param {string} category - The category name
 * @returns {string} - Formatted label
 */
function formatCategoryLabel(prefix, category) {
  const formattedCategory = category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const prefixLabel = prefix === 'env' ? 'Environment' : 'Branding';

  return `${prefixLabel}: ${formattedCategory}`;
}

/**
 * Filters fields based on package tier
 * @param {Object} fields - The allFields object
 * @param {string} currentPackage - Current package tier (Lite, Basic, Pro)
 * @returns {Object} - Filtered fields
 */
export function filterFieldsByPackage(fields, currentPackage) {
  const packageHierarchy = {
    [PACKAGE_TIERS.LITE]: [PACKAGE_TIERS.LITE],
    [PACKAGE_TIERS.BASIC]: [PACKAGE_TIERS.LITE, PACKAGE_TIERS.BASIC],
    [PACKAGE_TIERS.PRO]: [PACKAGE_TIERS.LITE, PACKAGE_TIERS.BASIC, PACKAGE_TIERS.PRO],
  };

  const allowedPackages = packageHierarchy[currentPackage] || [];

  return Object.entries(fields).reduce((acc, [key, value]) => {
    if (allowedPackages.includes(value.package)) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

/**
 * Filters only editable fields
 * @param {Object} fields - The allFields object
 * @returns {Object} - Filtered editable fields
 */
export function filterEditableFields(fields) {
  return Object.entries(fields).reduce((acc, [key, value]) => {
    if (value.isEditable) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

/**
 * Parses data type string to extract base type
 * @param {string} dataType - The dataType string (e.g., "DataType::Required::String")
 * @returns {string} - Base type (String, Number, Boolean, Array, Object)
 */
export function parseDataType(dataType) {
  const parts = dataType.split('::');
  return parts[parts.length - 1];
}

/**
 * Checks if data type is required
 * @param {string} dataType - The dataType string
 * @returns {boolean} - True if required
 */
export function isDataTypeRequired(dataType) {
  return dataType.includes('Required');
}

/**
 * Check if user has access to specific fields based on their package tier
 * @param {Array} fields - Array of field objects to check
 * @param {string} userPackage - User's current package tier (Lite, Basic, Pro)
 * @param {Object} allFields - All available fields definition
 * @returns {boolean} - True if user has access to all fields
 */
export function checkFieldsAccess(fields, userPackage, allFields) {
  if (!userPackage || !allFields) {
    return true;
  }

  const packageHierarchy = {
    [PACKAGE_TIERS.LITE]: [PACKAGE_TIERS.LITE],
    [PACKAGE_TIERS.BASIC]: [PACKAGE_TIERS.LITE, PACKAGE_TIERS.BASIC],
    [PACKAGE_TIERS.PRO]: [PACKAGE_TIERS.LITE, PACKAGE_TIERS.BASIC, PACKAGE_TIERS.PRO],
  };

  const allowedPackages = packageHierarchy[userPackage] || [];

  return fields.every((field) => {
    const fieldDef = allFields[field.key];
    return fieldDef && allowedPackages.includes(fieldDef.package || PACKAGE_TIERS.LITE);
  });
}

/**
 * Get the required package tier for a set of fields
 * @param {Array} fields - Array of field objects to check
 * @param {Object} allFields - All available fields definition
 * @returns {string|null} - Required package tier (Lite, Basic, Pro) or null
 */
export function getRequiredPlan(fields, allFields) {
  if (!fields || !allFields) {
    return null;
  }

  const packageTierOrder = [PACKAGE_TIERS.LITE, PACKAGE_TIERS.BASIC, PACKAGE_TIERS.PRO];

  let highestTier = null;
  let highestTierIndex = -1;

  fields.forEach((field) => {
    const fieldDef = allFields[field.key];
    const fieldPackage = fieldDef?.package;

    if (fieldPackage) {
      const tierIndex = packageTierOrder.indexOf(fieldPackage);
      if (tierIndex > highestTierIndex) {
        highestTierIndex = tierIndex;
        highestTier = fieldPackage;
      }
    }
  });

  return highestTier;
}
